/**
 * Where a public S3 object is served from.
 *
 * The bucket arrives as an argument because the two sides read it from
 * different variables — `EXPO_PUBLIC_UPLOADS_BUCKET` in the app,
 * `UPLOADS_BUCKET` on the server — while the host they build is the same one.
 */
export function s3PublicBaseUrl(bucket: string | undefined): string {
  return `https://${bucket}.s3.eu-central-1.amazonaws.com`
}

/**
 * The sizes an asset is delivered in, for a library that has no derivatives
 * yet.
 *
 * `og` is the original and is not one of them, which is why a configurator
 * sync does not carry it. It is served only as the last resort below.
 */
export const SERVED_SIZES = ['sm', 'md', 'lg'] as const

export type ServedSize = (typeof SERVED_SIZES)[number]

/**
 * The original, which every asset keeps whatever else it has.
 *
 * The one file a surface can always name: the ladder is dropped as soon as an
 * asset is rendered, and a usage added after that has nothing of its own
 * until the queue reaches the asset.
 */
export const ORIGINAL_SIZE = 'og'

/**
 * Where one rendition of an asset lives, as a key inside the bucket.
 *
 * Said once, because two sides have to agree on it exactly: delivery reads
 * this key, and a sync copies the keys it names. One of them spelling it
 * differently is a picture that is there and not shown, or shown and not
 * there.
 */
export function renditionKey(
  asset: { uuid: string; path: string; format: string },
  size: ServedSize | typeof ORIGINAL_SIZE,
): string {
  return `${asset.path}/${asset.uuid}_${size}.${asset.format}`
}
